import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";
import { MenuOption, Table, TableColumn, TableMenu } from "../../components/Table";
import { formatDate } from "../../utils/date";
import Bill from "../../models/Bill";
import { clearBill, generatedBillNo, } from "../../utils/bill";
import BillModal from "./BillModal";
import { BillData } from "../../types/bill";
import { initAllStores } from "../../utils/cache";
import User from "../../models/User";
import Hotel from "../../models/Hotel";
import { useToast } from "../../context/ToastContext";
import { useModal } from "../../context/ConfirmModalContext";
import FullPageSpinner from "../../components/FullPageSpinner";
import Customer from "../../models/Customer";
import Booking from "../../models/Booking";
import Order from "../../models/Order";
import BookedRoom from "../../models/BookedRoom";
import { orderTotal } from "../../utils/orders-helper";
import { computeBookingSummary } from "../../utils/sales-summary";


interface GuestBillPopupProps {
    open: boolean;
    guest: Customer;
    onClose: () => void;
    bills: Bill[];
}


// ✅ Table columns
const columns: TableColumn[] = [
    { label: "S/N", key: "sn", responsive: "md" },
    { label: "Bill No", key: "billNumber", responsive: "md" },
    { label: "Created At", key: "createdAt", responsive: "md" },
    { label: "Discount", key: "discount" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
];

export default function GuestBillPopup({ open, guest, onClose, bills }: GuestBillPopupProps) {
    const { showToast } = useToast();
    const { openModal, closeModal } = useModal();
    const [showBill, setShowBill] = useState(false);
    const [loading, setLoading] = useState(false);
    const [guestBill, setGuestBill] = useState<BillData>({
        issuedBy: "",
        bookings: [],
        discount: 0,
        hotel: {
            id: "",
            name: "",
            email: "",
            phone: "",
            address: "",
            preference: { vatRate: 0, serviceCharge: 0 } as any
        },
        guestName: "",
        billNumber: ""
    });

    const [billList, setBillList] = useState<Bill[]>(bills);
    // const [summary, setSummary] = useState({ totalDiscount })

        React.useEffect(() => {
        if (bills && bills.length > 0) {
            setBillList(bills);
        }
    }, [bills]);

    if (!open) return null;


    // ✅ Determine if a bill is active
    const getStatus = (bill: any): "Cleared" | "Pending" => {
        const activeRoom = bill.bookings[0]?.bookedRooms?.every((r: BookedRoom) => r.isPaid);
        const paidOrder = bill.orders?.every((o: Order) => o.isPaid);

        return activeRoom && paidOrder ? "Cleared" : "Pending";
    };

    const totalDiscount = (bookings: BookedRoom[]): number => {
        const discount = (bookings ?? []).reduce(
            (sum: number, b: BookedRoom) => sum + (b.discount ?? 0), 0
        );

        return discount;
    }

    const loadGuestBill = async (bill: any) => {
        setLoading(true);
        try {
            const cache = await initAllStores();
            const user: User | null = await cache.getBy("data", { id: "user" });
            const hotel: Hotel | null = await cache.getBy("data", { id: "hotel" });

            if (!user || !hotel) {
                return openModal(
                    "Session Expired ⚠️",
                    "We couldn’t find your user information. Please log in again to continue.",
                    () => {
                        closeModal();
                        window.location.href = "/login";
                    },
                    false,
                    "Login Again"
                );
            }

            setGuestBill({
                issuedBy: user.firstName + " " + user.lastName,
                orders: bill.orders, discount: totalDiscount(bill?.booking?.bookedRooms ?? []),
                bookings: bill?.booking?.bookedRooms ?? [],
                hotel, guestName: guest.name, billNumber: bill.billNumber as string,
            });

            setShowBill(true);
        } catch (err: any) {
            // console.log(err);
            showToast(
                `Failed to generate ${guest || "the guest’s"} bill. Please try again shortly.`,
                { type: "error" }
            );

        } finally {
            setLoading(false);
        }
    }

    // ✅ Transform bills into display data
    const tableData = billList.map((bill, index) => {

        const { totalPaid, totalPending } = orderTotal(bill?.orders);

        const bookedRooms = (bill?.bookings as Booking[])[0]?.bookedRooms ?? [] as BookedRoom[];
        const {
            bookCash, bookTransfer, bookPos,
            bookDiscount, bookUnpaidDiscount,
            bookPending
        } = computeBookingSummary(bookedRooms);

        const totalBookingAmount = bookCash + bookTransfer + bookPos + bookPending;
        const totalOrderAmount = totalPaid + totalPending;

        const totalAmount = totalBookingAmount + totalOrderAmount;
        const totalDiscount = bookUnpaidDiscount + bookDiscount;
        return {
            id: bill.id,
            sn: index + 1,
            billNumber: generatedBillNo("BL", bill.billNo) ?? "—",
            createdAt: formatDate(bill.createdAt ?? ""),
            booking: (bill?.bookings as Booking[])[0],
            orders: bill.orders,
            discount: totalDiscount.toLocaleString(),
            amount: totalAmount.toLocaleString(),
            isPaid: getStatus(bill) === "Cleared",
            status: (
                <span
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full
                    text-sm font-medium ${getStatus(bill) === "Cleared"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-200 text-red-600"
                        }`}
                >
                    {/* {getStatus(bill) === "Active" ? "🟢" : "⚪"}  */}
                    {getStatus(bill)}
                </span>
            ),
        }
    });

    // ✅ Clear guest bill
    const clearGuestBill = async (billId: string) => {
        setLoading(true);
        try {
            await clearBill("bill", billId);
            showToast(`Guest bill cleared successfully!`, { type: "success" });

            // ✅ Update state after clearing bill
            setBillList((prev) =>
                prev.map((b) =>
                    b.id === billId
                        ? {
                            ...b,
                            orders: (b.orders ?? []).map((o) => ({ ...o, isPaid: true })),
                            bookings: (b.bookings ?? []).map((bk) => ({
                                ...bk,
                                bookedRooms: (bk.bookedRooms ?? []).map((r) => ({ ...r, isPaid: true })),
                            })),
                        }
                        : b
                )
            );
        } catch (err) {
            console.error(err);
            showToast(`Failed to clear guest bill. Please try again.`, { type: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fadeIn">

            {/* Bill Modal */}
            {showBill && (
                <BillModal
                    billNumber={guestBill.billNumber}
                    hotel={guestBill.hotel}
                    discount={guestBill.discount}
                    guestName={guestBill.guestName}
                    issuedBy={guestBill.issuedBy}
                    bookings={guestBill.bookings}
                    orders={guestBill.orders}
                    onClose={() => setShowBill(false)}
                />
            )}
            <div className="relative w-[90%] max-w-3xl bg-white rounded-lg shadow-xl animate-scaleIn overflow-hidden">

                <FullPageSpinner show={loading} />
                {/* Top Accent Bar */}
                <div className="absolute top-0 left-0 w-full h-1 bg-green-600"></div>

                {/* Header */}
                <div className="flex items-center justify-between border-b p-4 bg-gray-50">
                    <h2 className="text-lg font-semibold text-gray-800">
                        Guest Bills{guest.name ? ` — ${guest.name}` : ""}
                    </h2>

                    <button
                        className="text-gray-500 hover:text-red-500 transition"
                        onClick={onClose}
                    >
                        {FaTimes({ size: 18 })}
                    </button>
                </div>

                {/* Table Content */}
                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    {bills.length > 0 ? (
                        <Table
                            columns={columns}
                            data={tableData}
                            rowsPerPage={10}
                            tableMenu={(row: any, closeMenu) => (
                                <TableMenu
                                    row={row}
                                    options={[
                                        {
                                            label: "Details",
                                            onClick: (b: Bill) => {
                                                loadGuestBill(b);
                                            },
                                        },
                                        !row.isPaid
                                            ? {
                                                label: "Clear Bill",
                                                color: "text-yellow-600",
                                                onClick: (b: Bill) => {
                                                    // console.log(b)
                                                    return openModal(
                                                        "Clear Guest Bill 💳",
                                                        guest.name
                                                            ? `Are you sure you want to clear all outstanding bills for ${guest.name}?`
                                                            + "This will mark all their bookings and orders as fully paid."
                                                            : "Are you sure you want to clear all outstanding guest bills? This will mark all bookings and orders as fully paid.",
                                                        () => clearGuestBill(b.id),
                                                        true,
                                                        "Yes, Clear Bill",
                                                        "Cancel",
                                                        "blue"
                                                    );

                                                },
                                            } : null,
                                    ].filter(Boolean) as MenuOption[]}
                                    closeMenu={closeMenu}
                                />
                            )}
                        />
                    ) : (
                        <div className="text-center py-10">
                            <p className="text-gray-500 mb-1 text-sm">No bills found</p>
                            <p className="text-gray-400 text-xs">
                                Guest bills will appear once bookings are made.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {/* <div className="text-center text-gray-400 text-xs py-3 border-t bg-gray-50">
          © {new Date().getFullYear()} Wiventory. All rights reserved.
        </div> */}
            </div>

            {/* Animations */}
            <style>
                {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.25s ease-out;
          }
          .animate-scaleIn {
            animation: scaleIn 0.25s ease-out;
          }
        `}
            </style>
        </div>
    );
}
